import { find, get, isEmpty } from "lodash";
import omitDeep from "omit-deep-lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import useSubscription from "shared/services/subscriptions";
import { names } from "shared/services/subscriptions/constants";
import graphql from "utils/api/graphql";
import { UPDATE_PROJECT_LIGHT } from "utils/api/graphql/mutations/projects";
import { CREATE_QUALIFICATION } from "utils/api/graphql/mutations/qualifications";
import { TRACERS } from "utils/api/graphql/queries/tracers";
import { CONTACT_POINT_SYSTEM, TRACER_TYPES } from "utils/constants";
import { matchStatusesWithProcess } from "utils/helpers/projectStatus";
import View from "./View";

const CONTACT_CARD_PROJECTS_LIST_FILTER = {
  withSubscriptions: false,
  withContract: true,
  withProcess: false,
  withProcessStatuses: false,
  withTracers: false,
  withFlows: false,
  exclude: [
    "subscriptions",
    "process",
    "processStatuses",
    "flows",
    "tracers",
    "possibleStatuses",
    "conditions",
    "actions",
    "actionsConditions",
  ],
};

const ContactCard = ({
  open,
  project,
  setOpen,
  contactId,
  matchStatusesWithProcess: matchStatusesWithProcessProp,
  currentProject,
  setDrawerProject,
  tracers: tracersProp,
}) => {
  const [projects, setProjects] = useState({ count: 0, data: [] });
  const [loading, setLoading] = useState(true);
  const [isAssigning, setIsAssigning] = useState({});
  const [tracers, setTracers] = useState(tracersProp || []);

  useEffect(() => {
    if (tracersProp?.length) {
      setTracers(tracersProp);
    }
  }, [tracersProp]);

  useQuery(TRACERS, {
    skip: !!tracersProp?.length,
    fetchPolicy: "cache-first",
    variables: { where: { type: TRACER_TYPES.MANUAL } },
    onCompleted: ({ tracers: { data } }) => {
      setTracers(omitDeep(data, "__typename"));
    },
  });

  const resolveMatchStatusesWithProcess = useCallback(
    (statuses) => {
      if (matchStatusesWithProcessProp) {
        return matchStatusesWithProcessProp(statuses);
      }
      return matchStatusesWithProcess(statuses, tracers);
    },
    [matchStatusesWithProcessProp, tracers],
  );

  const [addQualification] = useMutation(CREATE_QUALIFICATION, {
    refetchQueries: [
      {
        query: graphql.queries.QUALIFIERS,
        awaitRefetchQueries: true,
        variables: {
          withDeleted: true,
          where: {
            qualifications: {
              contact: { id: contactId },
            },
          },
        },
      },
    ],
  });

  const [getContactProjects] = useLazyQuery(graphql.queries.PROJECTS_LIST, {
    fetchPolicy: "no-cache",
  });
  const onClose = () => {
    if (setDrawerProject) setDrawerProject(currentProject);
    setOpen(false);
  };
  const { data: qualifiers } = useQuery(graphql.queries.QUALIFIERS);
  const { data: currentQualifier } = useQuery(graphql.queries.QUALIFIER, {
    fetchPolicy: "no-cache",
    variables: {
      withDeleted: true,
      where: {
        AND: [
          {
            qualifications: {
              last: true,
            },
          },
          {
            qualifications: {
              contact: { id: contactId },
            },
          },
        ],
      },
    },
  });
  const { data } = useQuery(graphql.queries.CONTACT, {
    variables: { where: { id: contactId } },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
  });

  const contactProjectsCriteria = useMemo(() => {
    const contact = data?.contact;
    if (!contactId || !contact || contact.id !== contactId) {
      return null;
    }
    if (!Array.isArray(contact.telecoms)) {
      return null;
    }
    const email = get(
      find(contact.telecoms, { system: CONTACT_POINT_SYSTEM.EMAIL }),
      "value",
    );
    const firstname = get(contact.user, "firstname");
    const lastname = get(contact.user, "lastname");

    if (isEmpty(email) || isEmpty(firstname) || isEmpty(lastname)) {
      return { incomplete: true };
    }

    return { email, firstname, lastname };
  }, [contactId, data?.contact]);

  useEffect(() => {
    setProjects({ count: 0, data: [] });
    setLoading(true);
  }, [contactId]);

  const fetchContactProjects = useCallback(
    ({ email, firstname, lastname }, { onSettled } = {}) => {
      getContactProjects({
        fetchPolicy: "no-cache",
        variables: {
          where: {
            visible: true,
            AND: [
              {
                contact: {
                  user: {
                    firstname,
                  },
                },
              },
              {
                contact: {
                  user: {
                    lastname,
                  },
                },
              },
              {
                contact: {
                  telecoms: {
                    value: email,
                    system: CONTACT_POINT_SYSTEM.EMAIL,
                  },
                },
              },
            ],
          },
          ...CONTACT_CARD_PROJECTS_LIST_FILTER,
        },
        onCompleted: ({ projectsList }) => {
          setLoading(false);
          setProjects(omitDeep(projectsList, "__typename"));
          onSettled?.();
        },
        onError: () => {
          setLoading(false);
          onSettled?.();
        },
      });
    },
    [getContactProjects],
  );

  useEffect(() => {
    if (!open || !contactId || !contactProjectsCriteria) {
      return;
    }

    if (contactProjectsCriteria.incomplete) {
      setProjects({ count: 0, data: [] });
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchContactProjects(contactProjectsCriteria, {
      onSettled: () => {
        if (cancelled) {
          return;
        }
      },
    });

    return () => {
      cancelled = true;
    };
  }, [open, contactId, fetchContactProjects, contactProjectsCriteria]);
  useSubscription({ query: names.QUALIFIERS, variables: {} });

  const [updateProject] = useMutation(UPDATE_PROJECT_LIGHT, {
    update: (cache, { data: { updateProject: updated } }) => {
      cache.modify({
        id: cache.identify({ __typename: "Project", id: updated.id }),
        fields: {
          author: () => updated.author,
        },
      });
    },
  });
  const [active, setActive] = useState("INFORMATION");

  useEffect(() => {
    setActive("INFORMATION");
  }, [contactId]);

  const onAssign = ({ projectId, authorId }) => {
    setIsAssigning({ [projectId]: true });
    updateProject({
      variables: {
        where: { id: projectId },
        data: {
          author: { id: authorId },
          fields: { ...get(project, "fields", {}), isManualAssign: true },
        },
      },
      onCompleted: () => setTimeout(() => setIsAssigning({}), 1000),
    });
  };

  return (
    <View
      key={contactId}
      drawer={open}
      loading={loading}
      onClose={onClose}
      project={project}
      active={active}
      setActive={setActive}
      contact={omitDeep(get(data, "contact"), "__typename")}
      projects={projects}
      onAssign={onAssign}
      isAssigning={isAssigning}
      matchStatusesWithProcess={resolveMatchStatusesWithProcess}
      tracers={tracers}
      qualifiers={get(qualifiers, "qualifiers.data", [])}
      addQualification={addQualification}
      currentQualifier={currentQualifier}
    />
  );
};

export default React.memo(ContactCard);
